import { useCallback, useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { DOCUMENT_LIST_REPORT_PDF_VIEWER_HASH } from "@/services/tradingReports";
import { Button } from "@/components/ui/button";
import PdfJsCanvasViewer from "./PdfJsCanvasViewer";

/**
 * نمایش PDF در iframe با پارامترهای پرکاربرد نمای ابزار Acrobat/مرورگر
 * (هم‌راستا با aryazar_gitea)
 */
const VIEWER_FRAGMENT = "toolbar=1&navpanes=0&scrollbar=1&view=FitH";

function isIosLike() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function showMobileDownloadFallback() {
  if (isIosLike()) return true;
  if (typeof navigator === "undefined") return false;
  if (/Android/i.test(navigator.userAgent || "")) return true;
  if (/(webOS|BlackBerry|IEMobile|Opera Mini)/i.test(navigator.userAgent || "")) return true;
  return false;
}

function appendViewerFragment(trimmedUrl) {
  const lower = trimmedUrl.toLowerCase();
  if (lower.startsWith("blob:") || lower.startsWith("data:")) {
    return trimmedUrl;
  }

  const hashIdx = trimmedUrl.indexOf("#");
  if (hashIdx === -1) {
    return `${trimmedUrl}#${VIEWER_FRAGMENT}`;
  }

  const frag = trimmedUrl.slice(hashIdx + 1);
  const fragLc = frag.toLowerCase();
  if (fragLc.includes("toolbar=")) {
    return trimmedUrl;
  }
  return `${trimmedUrl}&${VIEWER_FRAGMENT}`;
}

function urlForExternalPdfOpen(trimmed) {
  if (trimmed.endsWith(DOCUMENT_LIST_REPORT_PDF_VIEWER_HASH)) {
    return trimmed.slice(0, -DOCUMENT_LIST_REPORT_PDF_VIEWER_HASH.length);
  }
  return trimmed;
}

function isBlobOrDataUrl(url) {
  return /^blob:|^data:/i.test(String(url || ""));
}

function PdfDownloadFooter({ href, fileName, standalone = false }) {
  const outer = standalone
    ? "w-full"
    : "shrink-0 border-t border-border bg-muted/40 px-3 py-3 sm:px-4";

  return (
    <div className={outer}>
      <div className="flex flex-col gap-3 rounded-xl border border-border/70 bg-card p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
            aria-hidden
          >
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 text-right">
            <p className="text-sm font-semibold leading-tight text-foreground">فایل گزارش</p>
            <p className="truncate text-xs text-muted-foreground" title={fileName}>
              {fileName}
            </p>
          </div>
        </div>
        <Button asChild size="lg" className="h-11 w-full shrink-0 sm:w-auto sm:min-w-[9rem]">
          <a href={href} download={fileName}>
            دانلود گزارش
          </a>
        </Button>
      </div>
    </div>
  );
}

export default function PdfIframeViewer({
  fileUrl,
  title = "PDF Viewer",
  downloadFileName = "gozaresh-list-asnad.pdf",
}) {
  const trimmed = fileUrl ? String(fileUrl).trim() : "";
  const iframeSrc = trimmed ? appendViewerFragment(trimmed) : "";
  const [canvasFailed, setCanvasFailed] = useState(false);
  const onCanvasError = useCallback(() => setCanvasFailed(true), []);

  useEffect(() => {
    setCanvasFailed(false);
  }, [trimmed]);

  if (!iframeSrc) {
    return (
      <div className="flex h-full min-h-[420px] w-full items-center justify-center rounded-md border bg-white p-4 text-sm text-muted-foreground">
        آدرس فایل PDF معتبر نیست.
      </div>
    );
  }

  const downloadHref = urlForExternalPdfOpen(trimmed);
  const isMobile = showMobileDownloadFallback();
  const isBlob = isBlobOrDataUrl(trimmed);
  const usePdfJs = isMobile && isBlob && !canvasFailed;

  if (isMobile && isBlob && canvasFailed) {
    return (
      <div className="flex min-h-[min(200px,40dvh)] w-full flex-1 flex-col justify-center rounded-md border bg-white p-4">
        <PdfDownloadFooter href={downloadHref} fileName={downloadFileName} standalone />
      </div>
    );
  }

  if (usePdfJs) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md border bg-white">
        <div className="relative flex min-h-[min(240px,50dvh)] flex-1 flex-col overflow-hidden">
          <PdfJsCanvasViewer fileUrl={trimmed} onLoadError={onCanvasError} />
        </div>
        <PdfDownloadFooter href={downloadHref} fileName={downloadFileName} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md border bg-white">
      <div className="relative min-h-[min(240px,50dvh)] flex-1 overflow-hidden">
        <iframe title={title} src={iframeSrc} className="absolute inset-0 h-full w-full border-0" />
      </div>
      {isMobile ? <PdfDownloadFooter href={downloadHref} fileName={downloadFileName} /> : null}
    </div>
  );
}
